import { useCallback, useMemo } from 'react';
import { useIoContext } from '~/contexts';
import { useServiceBillingDetails } from '~/hooks/reactQuery/queries/useServiceBillingDetails';
import { useLocaleKeys } from '~/i18n';
import { isAfterOrEqual, startOfDay } from '~/infra/utils/dates-fns';
import { serviceModel } from '~/models';
import { InlineStartBillingMenuItem } from '../InlineStartBillingControl';
export const inlineStartBillingControlCalloutsTextsPath = 'components.billingSettingsSection.inlineStartBillingControl.callouts';
export const useCallouts = (param)=>{
    let { testIds, service, relationshipEffectiveDate, selectedItem, isAmendedService, disabled = false } = param;
    const { InlineStartBillingControlTestIds } = testIds;
    const { getNow } = useIoContext();
    const { t: calloutsTexts } = useLocaleKeys(inlineStartBillingControlCalloutsTextsPath);
    const getToday = useCallback(()=>startOfDay(getNow()), [
        getNow
    ]);
    const isProrated = serviceModel.isProrated(service);
    const isBilledUpfront = serviceModel.isBilledUpfront(service);
    const startBillingAt = serviceModel.startBillingAt(service);
    const { nextBillingDate, periods } = useServiceBillingDetails({
        service,
        relationshipEffectiveDate,
        enabled: !disabled
    });
    const proposalSignedAfterStartDateText = calloutsTexts.proposalSignedAfterStartDate();
    const effectOnAcceptanceText = calloutsTexts.effectOnAcceptance();
    const startDateMisalignedText = calloutsTexts.startDateMisaligned();
    const priorPeriodsText = periods ? calloutsTexts.priorPeriods({
        periods
    }) : '';
    const shouldShowProrateWarning = useMemo(()=>{
        if (!isProrated && isBilledUpfront && startBillingAt && nextBillingDate) {
            return nextBillingDate.getTime() !== startBillingAt.getTime();
        }
        return false;
    }, [
        nextBillingDate,
        startBillingAt,
        isProrated,
        isBilledUpfront
    ]);
    return useMemo(()=>{
        const callouts = [];
        if (disabled || selectedItem !== InlineStartBillingMenuItem.SpecificDate || !startBillingAt) {
            return callouts;
        }
        if (!isAmendedService && isAfterOrEqual(startBillingAt, getToday())) {
            if (relationshipEffectiveDate) {
                callouts.push({
                    dataTestId: InlineStartBillingControlTestIds.Callouts.FutureDate(),
                    text: proposalSignedAfterStartDateText
                });
            } else {
                callouts.push({
                    dataTestId: InlineStartBillingControlTestIds.Callouts.OnAcceptanceDate(),
                    text: effectOnAcceptanceText
                });
            }
        }
        if (shouldShowProrateWarning && !disabled) {
            callouts.push({
                dataTestId: InlineStartBillingControlTestIds.Callouts.Proration(),
                text: startDateMisalignedText
            });
        }
        if (periods && periods > 0) {
            callouts.push({
                dataTestId: InlineStartBillingControlTestIds.Callouts.PriorPeriods(),
                text: priorPeriodsText
            });
        }
        return callouts;
    }, [
        selectedItem,
        startBillingAt,
        shouldShowProrateWarning,
        periods,
        relationshipEffectiveDate,
        isAmendedService,
        disabled,
        getToday,
        InlineStartBillingControlTestIds,
        proposalSignedAfterStartDateText,
        effectOnAcceptanceText,
        startDateMisalignedText,
        priorPeriodsText
    ]);
};
